import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Send,
  GraduationCap,
  Compass,
  ExternalLink,
  Maximize2,
  X,
  Minus,
  Plus,
  PanelLeft,
  Trash2,
  MessageSquare,
  ChevronDown,
  Check,
  Newspaper,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { ImageLightbox } from "@/components/ui/image-lightbox";
import { cn } from "@/lib/utils";
import { ASSISTANT_PRODUCT_NAME, ASSISTANT_PRODUCT_TAGLINE } from "./product";
import { useAssistantChat, type AssistantProfile } from "./useAssistantChat";

export const FLOW_HELP_PRODUCT_NAME = "Conoce Flow";
export const FLOW_HELP_PRODUCT_TAGLINE = "Aprende a usar Flow con ayuda de IA";
import { useGuideUpdatesUnread } from "@/hooks/useGuideUpdatesRealtime";
import { useAutoGrow } from "@/hooks/useAutoGrow";
import { GuideUpdatesPanel } from "./GuideUpdatesPanel";
import { GuideUpdatesMini } from "./GuideUpdatesMini";

type Variant = "page" | "widget";

interface Props {
  variant?: Variant;
  /** Lock the chat to a single profile (hides the mode selector). */
  forceProfile?: AssistantProfile;
  onClose?: () => void;
  onMinimize?: () => void;
  className?: string;
}

const MD_IMG_RE = /!\[([^\]]*)\]\(([^)]+)\)/g;

function renderTextWithImages(text: string) {
  const parts: React.ReactNode[] = [];
  let last = 0;
  let match: RegExpExecArray | null;
  while ((match = MD_IMG_RE.exec(text)) !== null) {
    if (match.index > last) {
      parts.push(text.slice(last, match.index));
    }
    parts.push(
      <ImageLightbox
        key={match.index}
        src={match[2]}
        alt={match[1]}
        className="my-2 rounded-lg max-h-60 w-auto object-contain"
      />,
    );
    last = match.index + match[0].length;
  }
  if (last < text.length) {
    parts.push(text.slice(last));
  }
  return parts;
}

function TypingDots() {
  return (
    <div className="flex items-center gap-1.5 px-1 py-3" aria-label="Escribiendo">
      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground/55 [animation-delay:0ms]" />
      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground/55 [animation-delay:150ms]" />
      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground/55 [animation-delay:300ms]" />
    </div>
  );
}

function SourcesDisclosure({ sources }: { sources: Array<{ excerpt: string }> }) {
  const [open, setOpen] = useState(false);
  const count = sources.length;

  return (
    <div className="space-y-1.5">
      <button
        type="button"
        className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground transition-colors hover:text-foreground"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <ChevronDown
          className={cn("h-3 w-3 transition-transform", open ? "rotate-0" : "-rotate-90")}
        />
        Fuentes
        <span className="tabular-nums text-muted-foreground/70">({count})</span>
      </button>
      {open && (
        <div className="space-y-1.5">
          {sources.map((s, j) => (
            <p
              key={j}
              className="border-l-2 border-border pl-3 text-xs leading-relaxed text-muted-foreground"
            >
              {s.excerpt}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

function groupThreads(threads: ReturnType<typeof useAssistantChat>["threads"]) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const weekAgo = new Date(today);
  weekAgo.setDate(weekAgo.getDate() - 7);

  const groups: { label: string; items: typeof threads }[] = [
    { label: "Hoy", items: [] },
    { label: "Últimos 7 días", items: [] },
    { label: "Anteriores", items: [] },
  ];
  for (const t of threads) {
    const d = new Date(t.updated_at);
    if (d >= today) groups[0].items.push(t);
    else if (d >= weekAgo) groups[1].items.push(t);
    else groups[2].items.push(t);
  }
  return groups.filter((g) => g.items.length > 0);
}

export function AssistantChatPanel({
  variant = "page",
  forceProfile,
  onClose,
  onMinimize,
  className,
}: Props) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const chat = useAssistantChat({ forceProfile });
  const { confirm, ConfirmDialog } = useConfirm();
  const confirmDeleteThread = async (id: string) => {
    const ok = await confirm({
      title: "¿Ocultar esta conversación?",
      description: "Dejarás de verla, pero tu formador o coordinador podrá seguir viéndola.",
      confirmLabel: "Ocultar",
      variant: "destructive",
    });
    if (ok) await chat.deleteThread(id);
  };
  const isWidget = variant === "widget";
  const isPlatformOnly = forceProfile === "platform";
  const productName = isPlatformOnly
    ? FLOW_HELP_PRODUCT_NAME
    : chat.activeCampaign?.name ?? ASSISTANT_PRODUCT_NAME;
  const productTagline = isPlatformOnly ? FLOW_HELP_PRODUCT_TAGLINE : ASSISTANT_PRODUCT_TAGLINE;
  const hasMessages = chat.messages.length > 0;
  const [historyOpen, setHistoryOpen] = useState(!isWidget);
  const [modeMenuOpen, setModeMenuOpen] = useState(false);
  const modeMenuRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  useAutoGrow(composerRef, chat.input, isWidget ? 144 : 192);
  const grouped = useMemo(() => groupThreads(chat.threads), [chat.threads]);
  const [updatesOpen, setUpdatesOpen] = useState(searchParams.get("tab") === "novedades");

  useEffect(() => {
    if (searchParams.get("tab") === "novedades") {
      setUpdatesOpen(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  const { count: unreadCount, markAllAsRead } = useGuideUpdatesUnread();

  const modeLabel = chat.profile === "campaign" ? "Campaña" : "Flow";
  const ModeIcon = chat.profile === "campaign" ? GraduationCap : Compass;

  const canSelect = (p: AssistantProfile) =>
    p === "campaign" ? !!chat.activeCampaign : !!chat.platformAssistant;
  const noWorkspaceGuia = forceProfile === "campaign" && !chat.loading && !chat.activeCampaign;

  useEffect(() => {
    if (!modeMenuOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!modeMenuRef.current?.contains(e.target as Node)) {
        setModeMenuOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setModeMenuOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [modeMenuOpen]);

  const composer = (
    <div
      className={cn(
        "relative rounded-2xl border border-border bg-background",
        "shadow-[0_8px_30px_rgba(0,0,0,0.06)]",
        "focus-within:border-foreground/20",
        isWidget ? "min-h-[96px]" : "min-h-[132px]",
      )}
    >
      {chat.activeSection && (
        <div className="flex items-center gap-1.5 px-4 pt-3 pb-0">
          <span className="inline-flex items-center gap-1 rounded-md bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
            <GraduationCap className="h-3 w-3" />
            {chat.activeSection}
          </span>
          <button
            type="button"
            className="text-xs text-muted-foreground hover:text-foreground"
            onClick={() => chat.newChat()}
          >
            ✕
          </button>
        </div>
      )}
      <textarea
        rows={isWidget ? 2 : 3}
        className={cn(
          "w-full resize-none bg-transparent px-4 text-[15px] leading-6 outline-none",
          chat.activeSection ? "pt-2" : "pt-4",
          "placeholder:text-muted-foreground/65 caret-foreground",
          isWidget ? "min-h-[52px] max-h-36" : "min-h-[72px] max-h-48",
        )}
        placeholder={
          chat.activeSection
            ? `Pregunta sobre ${chat.activeSection}…`
            : chat.profile === "campaign"
              ? "Pregunta sobre la campaña…"
              : "Pregunta cómo usar Flow…"
        }
        value={chat.input}
        disabled={chat.loading}
        ref={composerRef}
        onChange={(e) => chat.setInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            void chat.send();
          }
        }}
      />
      <div className="flex items-center justify-between gap-2 px-3 pb-3 pt-1">
        {/* Inline menu (no portal) — Radix dropdown breaks inside widget pointer-events/overflow shells */}
        {!chat.isProfileLocked && (
        <div className="relative" ref={modeMenuRef}>
          <button
            type="button"
            aria-haspopup="listbox"
            aria-expanded={modeMenuOpen}
            className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            onClick={() => setModeMenuOpen((v) => !v)}
          >
            <ModeIcon className="h-3.5 w-3.5" />
            {modeLabel}
            <ChevronDown
              className={cn("h-3 w-3 opacity-60 transition-transform", modeMenuOpen && "rotate-180")}
            />
          </button>
          {modeMenuOpen && (
            <div
              role="listbox"
              className="absolute bottom-full left-0 z-[80] mb-1.5 w-48 overflow-hidden rounded-xl border bg-popover p-1 text-popover-foreground shadow-lg"
            >
              {(
                [
                  {
                    id: "campaign" as const,
                    label: "Campaña",
                    Icon: GraduationCap,
                  },
                  {
                    id: "platform" as const,
                    label: "Flow",
                    Icon: Compass,
                  },
                ] as const
              ).map(({ id, label, Icon }) => {
                const enabled = canSelect(id);
                const active = chat.profile === id;
                return (
                  <button
                    key={id}
                    type="button"
                    role="option"
                    aria-selected={active}
                    disabled={!enabled}
                    className={cn(
                      "flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm transition-colors",
                      enabled ? "hover:bg-muted" : "cursor-not-allowed opacity-40",
                      active && "bg-muted/70",
                    )}
                    onClick={() => {
                      if (!enabled) return;
                      chat.setProfile(id);
                      setModeMenuOpen(false);
                    }}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    <span className="flex-1">{label}</span>
                    {active && <Check className="h-3.5 w-3.5 text-foreground" />}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        )}

        <Button
          size="icon"
          className="ml-auto h-9 w-9 shrink-0 rounded-full"
          onClick={() => void chat.send()}
          disabled={chat.sending || !chat.input.trim()}
        >
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );

  const historyRail = (
    <aside
      className={cn(
        "flex flex-col border-r bg-muted/20",
        isWidget ? "w-full border-r-0 border-b" : "w-[260px] shrink-0",
        !historyOpen && !isWidget && "hidden",
      )}
    >
      <div className="flex items-center gap-2 p-3 shrink-0">
        <Button
          size="sm"
          className="h-8 flex-1 justify-start gap-2 rounded-lg"
          onClick={() => chat.newChat()}
        >
          <Plus className="h-3.5 w-3.5" />
          Nueva conversación
        </Button>
      </div>

      {/* Conversations — top half, own scroll */}
      <div className={cn(
        "min-h-0 space-y-4 overflow-y-auto px-2 pb-3",
        isWidget ? "max-h-40" : "flex-1 basis-1/2",
      )}>
        {chat.threads.length === 0 && (
          <p className="px-2 py-4 text-center text-xs text-muted-foreground">
            Aún no hay conversaciones guardadas.
          </p>
        )}
        {grouped.map((group) => (
          <div key={group.label}>
            <div className="px-2 pb-1 text-[11px] font-medium text-muted-foreground">
              {group.label}
            </div>
            <div className="space-y-0.5">
              {group.items.map((t) => (
                <div
                  key={t.id}
                  className={cn(
                    "group flex items-center gap-1 rounded-lg px-2 py-1.5 text-sm transition-colors",
                    chat.threadId === t.id
                      ? "bg-background shadow-sm"
                      : "hover:bg-background/70",
                  )}
                >
                  <button
                    type="button"
                    className="min-w-0 flex-1 truncate text-left"
                    onClick={() => void chat.openThread(t.id)}
                  >
                    <span className="flex items-center gap-1.5">
                      <MessageSquare className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      <span className="truncate">{t.title}</span>
                    </span>
                  </button>
                  <button
                    type="button"
                    className="rounded p-1 text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100"
                    title="Ocultar"
                    aria-label="Ocultar conversación"
                    onClick={() => void confirmDeleteThread(t.id)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Novedades — bottom half, own scroll */}
      {!isWidget && (
        <div className="flex min-h-0 flex-1 basis-1/2 flex-col border-t">
          <div className="flex items-center gap-1.5 px-3 py-2 shrink-0">
            <Newspaper className="h-3.5 w-3.5 text-muted-foreground" />
            <h3 className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">Novedades</h3>
            {unreadCount > 0 && (
              <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-medium text-primary-foreground">
                {unreadCount}
              </span>
            )}
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-3 space-y-1.5">
            <GuideUpdatesMini
              assistantId={chat.profile === "campaign" ? (chat.campaignId ?? null) : null}
            />
          </div>
        </div>
      )}
    </aside>
  );

  const messages = (
    <div
      className={cn(
        "mx-auto flex w-full flex-1 flex-col overflow-y-auto",
        isWidget ? "max-w-none px-3 py-3" : "max-w-2xl px-4 py-6",
      )}
    >
      <div className="flex-1 space-y-6">
        {(chat.loading || chat.loadingThread) && (
          <div className="flex justify-center py-16">
            <TypingDots />
          </div>
        )}
        {!chat.loading &&
          !chat.loadingThread &&
          chat.messages.map((m, i) =>
            m.role === "user" ? (
              <div key={m.id ?? `u-${i}`} className="flex justify-end">
                <div className="max-w-[min(85%,36rem)] rounded-2xl rounded-br-md bg-muted px-3.5 py-2 text-[15px] leading-relaxed whitespace-pre-wrap">
                  {m.content}
                </div>
              </div>
            ) : (
              <div key={m.id ?? `a-${i}`} className="max-w-[min(92%,40rem)] space-y-3">
                <p className="whitespace-pre-wrap text-[15px] leading-relaxed">{renderTextWithImages(m.content)}</p>
                {m.sources && m.sources.length > 0 && (
                  <SourcesDisclosure sources={m.sources} />
                )}
                {m.actions && m.actions.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {m.actions.map((a, j) => {
                      if (a.type === "navigate" && a.path) {
                        return (
                          <button
                            key={j}
                            type="button"
                            className="inline-flex items-center gap-1.5 rounded-md border bg-background px-2.5 py-1 text-xs font-medium hover:bg-muted"
                            onClick={() => {
                              navigate(a.path!);
                              onClose?.();
                            }}
                          >
                            <ExternalLink className="h-3 w-3" />
                            {a.label || a.path}
                          </button>
                        );
                      }
                      if (a.type === "study_section" && a.source_id && a.label) {
                        return (
                          <button
                            key={j}
                            type="button"
                            className="inline-flex items-center gap-1.5 rounded-md border bg-background px-2.5 py-1 text-xs font-medium hover:bg-muted"
                            onClick={() => chat.openStudySection(a.source_id!, a.label!)}
                            disabled={chat.sending}
                          >
                            <GraduationCap className="h-3 w-3" />
                            {a.label}
                          </button>
                        );
                      }
                      if (a.type === "study_index") {
                        return (
                          <button
                            key={j}
                            type="button"
                            className="inline-flex items-center gap-1.5 rounded-md border bg-background px-2.5 py-1 text-xs font-medium hover:bg-muted"
                            onClick={() => chat.newChat()}
                            disabled={chat.sending}
                          >
                            <GraduationCap className="h-3 w-3" />
                            {a.label || "Volver al inicio"}
                          </button>
                        );
                      }
                      return null;
                    })}
                  </div>
                )}
              </div>
            ),
          )}
        {chat.sending && <TypingDots />}
        <div ref={chat.bottomRef} />
      </div>
    </div>
  );

  const noGuiaState = (
    <div className="flex flex-1 flex-col items-center justify-center px-4 py-10">
      <div className="space-y-2 text-center">
        <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">{ASSISTANT_PRODUCT_NAME}</h1>
        <p className="text-sm text-muted-foreground">Este workspace aún no tiene GuIA.</p>
      </div>
    </div>
  );

  const emptyState = noWorkspaceGuia ? noGuiaState : (
    <div className="flex flex-1 flex-col items-center justify-center px-4 py-10">
      <div className="mb-10 space-y-2 text-center">
        <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">
          {productName}
        </h1>
        <p className="text-sm text-muted-foreground">{productTagline}</p>
      </div>
      <div className={cn("w-full", isWidget ? "max-w-none" : "max-w-2xl")}>{composer}</div>
      <div className="mt-8 grid w-full max-w-2xl gap-2 sm:grid-cols-3">
        {chat.profile === "campaign" && chat.studySections.length > 0
          ? chat.studySections.map((section) => (
              <button
                key={section.label}
                type="button"
                className="rounded-xl border bg-background px-3 py-3 text-left text-xs leading-snug text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                onClick={() => chat.enterSection(section.label)}
                disabled={chat.sending}
              >
                <GraduationCap className="mb-1 h-3.5 w-3.5" />
                {section.label}
              </button>
            ))
          : [
              chat.profile === "campaign"
                ? "¿Por dónde empiezo?"
                : "¿Dónde configuro los roles?",
              chat.profile === "campaign"
                ? "¿Cuál es la promoción VIP?"
                : "Llévame a Configuración → Matriz",
              chat.profile === "campaign"
                ? "Resume las políticas de la campaña"
                : "¿Cómo cambio de workspace?",
            ].map((example) => (
              <button
                key={example}
                type="button"
                className="rounded-xl border bg-background px-3 py-3 text-left text-xs leading-snug text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                onClick={() => {
                  if (example === "¿Por dónde empiezo?") {
                    void chat.send({ text: example });
                  } else {
                    chat.setInput(example);
                  }
                }}
              >
                {example}
              </button>
            ))}
      </div>
    </div>
  );

  if (isWidget) {
    return (
      <div
        className={cn(
          "flex h-full flex-col overflow-hidden rounded-xl border bg-background shadow-2xl",
          className,
        )}
      >
        <ConfirmDialog />
        <header className="flex items-center gap-1 border-b px-2 py-2">
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8"
            title="Historial"
            onClick={() => setHistoryOpen((v) => !v)}
          >
            <PanelLeft className="h-3.5 w-3.5" />
          </Button>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold">{productName}</div>
          </div>
          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => chat.newChat()}>
            <Plus className="h-3.5 w-3.5" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8 relative"
            title="Novedades"
            onClick={() => setUpdatesOpen((v) => !v)}
          >
            <Newspaper className="h-3.5 w-3.5" />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-medium text-primary-foreground">
                {unreadCount}
              </span>
            )}
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8"
            title="Pantalla completa"
            onClick={() => {
              navigate("/app/assistant");
              onClose?.();
            }}
          >
            <Maximize2 className="h-3.5 w-3.5" />
          </Button>
          {onMinimize && (
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={onMinimize}>
              <Minus className="h-3.5 w-3.5" />
            </Button>
          )}
          {onClose && (
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={onClose}>
              <X className="h-3.5 w-3.5" />
            </Button>
          )}
        </header>
        {historyOpen && historyRail}
        {updatesOpen && (
          <div className="flex-1 min-h-0">
            <GuideUpdatesPanel
              assistantId={chat.profile === "campaign" ? (chat.campaignId ?? null) : null}
              onClose={() => setUpdatesOpen(false)}
              onMarkRead={markAllAsRead}
            />
          </div>
        )}
        <div className={cn("flex min-h-0 flex-1 flex-col", updatesOpen && "hidden")}>
          {!hasMessages && !chat.loadingThread ? emptyState : messages}
          {(hasMessages || chat.sending) && <div className="shrink-0 border-t p-2.5">{composer}</div>}
        </div>
      </div>
    );
  }

  return (
    <div className={cn("flex h-full min-h-0 overflow-hidden", className)}>
      <ConfirmDialog />
      {historyRail}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <div className="flex items-center gap-2 px-3 py-2">
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8"
            title={historyOpen ? "Ocultar historial" : "Mostrar historial"}
            onClick={() => setHistoryOpen((v) => !v)}
          >
            <PanelLeft className="h-4 w-4" />
          </Button>
          {!historyOpen && (
            <Button size="sm" variant="ghost" className="h-8 gap-1.5" onClick={() => chat.newChat()}>
              <Plus className="h-3.5 w-3.5" />
              Nueva
            </Button>
          )}
          {chat.activeCampaign && chat.profile === "campaign" && (
            <span className="ml-auto text-xs text-muted-foreground">
              {chat.activeCampaign.name}
            </span>
          )}
        </div>

        {!hasMessages && !chat.loadingThread ? (
          emptyState
        ) : (
          <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            {messages}
            <div className="mx-auto w-full max-w-2xl shrink-0 px-4 pb-5 pt-2">{composer}</div>
          </div>
        )}
      </div>
    </div>
  );
}
